import React, { useState, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  isLoading = false,
  placeholder,
}) => {
  const { t } = useTranslation('common');
  const resolvedPlaceholder = placeholder || t('Ask me anything...');
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = () => {
    if (message.trim() && !disabled && !isLoading) {
      onSendMessage(message);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        // Keep the caret in the composer so the user can keep typing without re-clicking.
        textareaRef.current.focus();
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 96)}px`;
  };

  return (
    <div className="flex gap-2 p-3 border-t border-border bg-card">
      <textarea
        ref={textareaRef}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        placeholder={resolvedPlaceholder}
        aria-label={t('Message the assistant')}
        disabled={disabled || isLoading}
        rows={1}
        className="flex-1 resize-none rounded-lg border border-border bg-muted px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus:outline-none hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ maxHeight: '96px', overflowY: 'auto' }}
      />
      <button
        onClick={handleSend}
        disabled={!message.trim() || disabled || isLoading}
        aria-label={t('Send message')}
        title={t('Send message')}
        className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {isLoading ? (
          <Icon name="Loader2" size={20} className="animate-spin" />
        ) : (
          <Icon name="Send" size={20} />
        )}
      </button>
    </div>
  );
};

export default ChatInput;
